import React, { useEffect, useRef, useState } from "react";
import {SubmitHandler, useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../store';
import ReCAPTCHA from "react-google-recaptcha";
import { DropdownItem, FinishSignUpFormData } from "../../util/interfaces";
import {
  yearlyRevenueOptions, defaultMessageObj, usStates, employeeCounts, businessWaitlistGateKeys, orgTypes
} from "../../util/constants";
import {AuthEffects} from "../../store/auth";
import {
  FormCard,
  StyledField,
  Form,
  SubmitButton,
  StyledA, NameInput, NameContainer, CheckboxCopy, CaptchaLegal, FormCardHeader,
  CaptchaContainer,
  Line
} from "styles/styled";
import { Dropdown, TextInput, Checkbox, FilteredDropdown } from "shared-react";
import styled from "styled-components";
import MessageCard from "../message-card";
import {AppEffects} from "../../store/app";
import { FormCardH2 } from "../businessSignup";
import BusinessWaitlist from "../businessSignup/businessWaitlist";

const stateOptions: DropdownItem[] = [{value: '', label: 'Select State', hidden: true}];
for (let state of usStates) {
  stateOptions.push({value: state.name, label: state.name});
}
const employeeCountOptions: DropdownItem[] = [{value: '', label: 'Select Count', hidden: true}];
for (let count of employeeCounts) {
  employeeCountOptions.push({value: count.value, label: count.label});
}
const annualRevenueOptions: DropdownItem[] = [{value: '', label: 'Select Revenue', hidden: true}];
for (let option of yearlyRevenueOptions) {
  annualRevenueOptions.push(option);
}
const orgTypeOptions: DropdownItem[] = [{value: '', label: 'Select Organization Type', hidden: true}];
for (let option of orgTypes) {
  orgTypeOptions.push(option);
}

const CustomerOption = styled.div `{
  margin: 0.25rem 1rem 0.25rem 0;
  display: flex;
}`;

const Spacer = styled.div `{
  min-width: 30px;
}`;

const schema = yup.object().shape({
  givenName: yup.string().required('First Name is required.'),
  familyName: yup.string().required('Last Name is required.'),
  organizationName: yup.string().required('Please enter the name of your organization.'),
  country: yup.string().required('Please enter your country of residence.'),
  state: yup.string().when('country', {
    is: 'United States',
    then: yup.string().required('Please enter your state of residence.')
  }),
  industry: yup.string().required('Please select an industry.'),
  orgType: yup.string().required('Please select an organization type.'),
  B2B: yup.boolean(),
  B2C: yup.boolean(),
  customerType: yup.string().test(
    'customerType',
    'Customer Type is required.',
    function() {
      const { B2B, B2C } = this.parent;
      return B2B || B2C;
    }
  ),
  yearlyRevenue: yup.string().not(['Default Value']).required('Annual revenue is required.'),
  employeeCount: yup.string().not(['Default Value']).required('Employee count is required.'),
  website: yup.string().matches(
    /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
    'Please enter a valid website.'
  ).required('Website is required.'),
  marketingOptInStatus: yup.boolean(),
  agreedToTOSandPP: yup.boolean().isTrue('Please agree to the Terms of Service and Privacy Policy.'),
});

const FinishSignup = () => {

  const dispatch = useDispatch();
  const [showWaitlist, setShowWaitlist] = useState(true);
  const industries = useSelector((state: RootState) => state.app.industries);
  const countries = useSelector((state: RootState) => state.app.countries);
  const captchaKey = useSelector((state: RootState) => state.auth.captchaKey);
  const signupErrorMessage = useSelector((state: RootState) => state.auth.signupErrorMessage);
  const accountErrorMessage = useSelector((state: RootState) => state.account.accountError);
  const captchaDisabled = useSelector((state: RootState) => state.auth.captchaDisabled);
  const session = useSelector((state: RootState) => state.auth.session);
  const queryParams = useSelector((state: RootState) => state.app.queryParams);
  const initComplete = useSelector((state: RootState) => state.app.initComplete);
  const { register, handleSubmit, watch, control, setValue, getValues, formState: { errors }, trigger } =useForm(
    {
      resolver: yupResolver(schema)
    });

  const industryOptions: DropdownItem[] = [{value: '', label: 'Select Industry', hidden: true}];
  for (let industry of industries) {
    industryOptions.push({value: industry.name, label: industry.name, alts: industry.alts});
  }
  const countryOptions: DropdownItem[] = [{value: '', label: 'Select Country', hidden: true}];
  for (let country of countries) {
    countryOptions.push({value: country.name, label: country.name, alts: country.alts});
  }

  const isValidFactory = (optionsArray: DropdownItem[]) => {
    return (target: string) => {
      return optionsArray.some((option) => option.value.toLowerCase() === target.toLowerCase());
    }
  };
  // These are being used to ensure country/state are only set if they are valid options
  const isValidCountry = isValidFactory(countryOptions);
  const isValidState = isValidFactory(stateOptions);

  const B2B = watch("B2B");
  const B2C = watch("B2C");

  useEffect(() => {
    if (B2B || B2C) {
      trigger("customerType");
    }
  }, [B2B, B2C, trigger]);

  let recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    determineShowWaitlist();
    dispatch(AppEffects.getIndustries());
    dispatch(AppEffects.getCountries());
    // they query params are not always parsed at render time, so this logic must be added to useEffect
    const {organizationName, firstName, lastName, country, state,} = queryParams;
    organizationName && setValue('organizationName', decodeURIComponent(organizationName));
    firstName && setValue('givenName', decodeURIComponent(firstName));
    lastName && setValue('familyName', decodeURIComponent(lastName));
    country && isValidCountry(decodeURIComponent(country)) && setValue('country', decodeURIComponent(country));
    state && isValidState(decodeURIComponent(state)) && setValue('state', decodeURIComponent(state));
  }, [queryParams, setValue]);

  useEffect(() => {
    if(initComplete) {
      if (session?.accessTypes.includes('merchant')) {
        dispatch(AppEffects.redirect('/dashboard/getstarted'));
      } else if (!session.isAuthenticated) {
        dispatch(AppEffects.redirect('/authenticate/login'));
      }
    }
  }, [session, initComplete, dispatch])

  const onSubmit: SubmitHandler<FinishSignUpFormData> = async (data: FinishSignUpFormData) => {
    let gCaptchaResponse: string | null = null;
    if(!captchaDisabled && recaptchaRef.current){
      gCaptchaResponse = await recaptchaRef.current.executeAsync()
      recaptchaRef.current.reset();
    }

    dispatch(AuthEffects.startFinishSignup({...data, gCaptchaResponse, referrerId: queryParams.referrerId}));
  };

  let displayErrorMessage
  if(!signupErrorMessage && !accountErrorMessage){
    displayErrorMessage = null;
  } else if (accountErrorMessage) {
    displayErrorMessage = accountErrorMessage
  } else {
    displayErrorMessage = <span>Account upgrade failed. Please contact <StyledA
      aria-label="email support" href="mailto:support@bitpay.com">support</StyledA> if you're having trouble upgrading your account.</span>;
  }

  const determineShowWaitlist = () => {
    setShowWaitlist(!businessWaitlistGateKeys.includes(queryParams.apply));
  };

  return (
    <FormCard>
      <FormCardHeader>Finish Creating Your Business Account</FormCardHeader>
      <Line/>
      <MessageCard type={defaultMessageObj.type}>
        {displayErrorMessage}
      </MessageCard>
      { showWaitlist ?
        <BusinessWaitlist></BusinessWaitlist>
      :
        <Form onSubmit={handleSubmit(onSubmit)}>
          <NameContainer>
            <StyledField errorMessage={errors.givenName?.message} error={errors.givenName} label="First Name" style={{marginRight: '30px'}}>
              <NameInput aria-label="First Name" autoComplete="disable" {...register('givenName')}/>
            </StyledField>
            <Spacer/>
            <StyledField errorMessage={errors.familyName?.message} error={errors.familyName} label="Last Name">
              <NameInput aria-label="Last Name" autoComplete="disable" {...register('familyName')}/>
            </StyledField>
          </NameContainer>
          <FormCardH2>Business Details</FormCardH2>
          <StyledField errorMessage={errors.organizationName?.message} error={errors.organizationName} label="Legal Business Name">
            <TextInput aria-label="Company" autoComplete="disable" {...register('organizationName')}/>
          </StyledField>
          <StyledField errorMessage={errors.country?.message} error={errors.country} label="Select a Country">
            <Controller name="country" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <FilteredDropdown ref={ref} value={getValues().country} onChange={onChange} aria-label="Select a Country" autoComplete="disable" defaultValue={countryOptions[0].value} items={countryOptions}/>
                        }
                        } />
          </StyledField>
          {watch('country') === 'United States' &&
          <StyledField errorMessage={errors.state?.message} error={errors.state} label="State of Residence">
            <Controller name="state" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <FilteredDropdown ref={ref} value={getValues().state} onChange={onChange} autoComplete="disable" aria-label="State of Residence" defaultValue={stateOptions[0].value} items={stateOptions}/>
                        }
                        } />
          </StyledField>}
          <StyledField errorMessage={errors.industry?.message} error={errors.industry} label="Select an Industry">
            <Controller name="industry" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <FilteredDropdown ref={ref} value={getValues().industry} onChange={onChange} aria-label="Select an Industry" autoComplete="disable" defaultValue={industryOptions[0].value} items={industryOptions}/>
                        }
                        } />
          </StyledField>
          <StyledField errorMessage={errors.orgType?.message} error={errors.orgType} label="Organization Type">
          <Controller name="orgType" control={control}
                      render={({ field: { onChange, ref } }) => {
                        return <FilteredDropdown ref={ref} value={getValues().orgType} onChange={onChange} aria-label="Select an Organization Type" autoComplete="disable" defaultValue={orgTypeOptions[0].value} items={orgTypeOptions}/>
                      }
                      } />
        </StyledField>
          <NameContainer>
            <StyledField errorMessage={errors.employeeCount?.message} error={errors.employeeCount} label="Employee Count">
              <Controller name="employeeCount" control={control}
                          render={({ field: { onChange, ref } }) => {
                            return <Dropdown ref={ref} onChange={onChange} aria-label="Employee Count" defaultValue={employeeCountOptions[0].value} items={employeeCountOptions}/>
                          }
                          } />
            </StyledField>
            <Spacer/>
            <StyledField errorMessage={errors.yearlyRevenue?.message} error={errors.yearlyRevenue} label="Annual Revenue">
              <Controller name="yearlyRevenue" control={control}
                          render={({ field: { onChange, ref } }) => {
                            return <Dropdown ref={ref} onChange={onChange} aria-label="Annual Revenue" defaultValue={annualRevenueOptions[0].value} items={annualRevenueOptions}/>
                          }
                          } />
            </StyledField>
          </NameContainer>
          <StyledField errorMessage={errors.customerType?.message} error={errors.customerType} label="Who are your customers">
            <CustomerOption>
              <Controller name="B2C" control={control}
                          render={({ field: { onChange, ref } }) => {
                            return (
                              <Checkbox checked={getValues().B2C} aria-label="Consumer Checkbox" onChange={onChange} ref={ref}/>

                            )
                          }} />
              <CheckboxCopy>Consumers</CheckboxCopy>
            </CustomerOption>
            <CustomerOption>
              <Controller name="B2B" control={control}
                          render={({ field: { onChange, ref } }) => {
                            return <Checkbox checked={getValues().B2B} aria-label="Business Checkbox" onChange={onChange} ref={ref}/>
                          }} />
              <CheckboxCopy>Businesses</CheckboxCopy>
            </CustomerOption>
          </StyledField>
          <StyledField errorMessage={errors.website?.message} error={errors.website} label="Website">
            <TextInput aria-label="Website" type="website" readOnly={!!queryParams.website} autoComplete="disable" {...register('website')}/>
          </StyledField>
          <StyledField errorMessage={errors.agreedToTOSandPP?.message} error={errors.agreedToTOSandPP}>
            <Controller name="agreedToTOSandPP" control={control}
                        render={({ field: { onChange, ref } }) => {

                          return <Checkbox aria-label="Agree to Terms of Service and Privacy Policy" onChange={onChange} ref={ref}/>

                        }} />
            <CheckboxCopy>I agree to the <StyledA href="/legal/#terms-of-use" target="_blank">Terms of Service</StyledA> and <StyledA href="/about/privacy/" target="_blank">Privacy Policy</StyledA>.</CheckboxCopy>
          </StyledField>
          <StyledField errorMessage={errors.marketingOptInStatus?.message} error={errors.marketingOptInStatus}>
            <Controller name="marketingOptInStatus" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <Checkbox onChange={onChange} ref={ref}/>
                        }} />
            <CheckboxCopy>I agree to receive marketing communications from BitPay</CheckboxCopy>
          </StyledField>
          <CaptchaContainer>
            {!captchaDisabled &&
            <ReCAPTCHA
              sitekey={captchaKey}
              size="invisible"
              badge="inline"
              ref={recaptchaRef}
            />}
          </CaptchaContainer>
          <SubmitButton type="submit" aria-label="submit" role="button">Submit</SubmitButton>
          {!captchaDisabled && <CaptchaLegal>
            This site is protected by reCAPTCHA and the Google
            <StyledA
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer noopener"
            > Privacy Policy </StyledA>
            and
            <StyledA
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer noopener"
            > Terms of Service </StyledA>
            apply.
          </CaptchaLegal>}
        </Form>
      }
    </FormCard>
  )
};

export default FinishSignup;
